import { useMutation } from '@tanstack/react-query';
import api from '../api';
import { CHALLENGE } from '@/types/auth';
import { z } from 'zod';
import { queryClient } from '@/providers/QueryProvider';
import { getCurrentUserQueryOptions } from '@/api/queries/users';

interface LoginParams {
  username: string;
  password: string;
}

const AuthTokensSchema = z.object({
  accessToken: z.string(),
  idToken: z.string(),
  expiresIn: z.number().optional(),
});

const ChallengeResponseSchema = z.object({
  challengeName: z.nativeEnum(CHALLENGE),
  session: z.string(),
});

export const LoginResponseSchema = z.union([ z.object({ tokens: AuthTokensSchema }), ChallengeResponseSchema ]);

export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export const useLoginMutation = () => {
  return useMutation({
    mutationFn: async ({ username, password }: LoginParams) => {
      const response = await api.post('auth/login', {
        body: JSON.stringify({ username: username.toLocaleLowerCase(), password }),
        credentials: 'include',
      });
      return LoginResponseSchema.parse(await response.json());
    },
  });
};

interface ForgotPasswordParams {
  username: string;
}

export const useForgotPasswordMutation = () => {
  return useMutation({
    mutationFn: async ({ username }: ForgotPasswordParams) => {
      const response = await api.post('auth/forgot-password', {
        body: JSON.stringify({ username }),
      });
      return await response.json();
    },
  });
};

interface ResetPasswordParams {
  username: string;
  confirmationCode: string;
  newPassword: string;
}

export const useResetPasswordMutation = () => {
  return useMutation({
    mutationFn: async (params: ResetPasswordParams) => {
      const response = await api.post('auth/reset-password', {
        body: JSON.stringify(params),
      });
      return await response.json();
    },
  });
};

interface SetNewPasswordParams {
  username: string;
  newPassword: string;
  session: string;
}

export const TokensReponseSchema = z.object({
  tokens: AuthTokensSchema,
});
export type AuthTokens = z.infer<typeof AuthTokensSchema>;

export const SetNewPasswordResponseSchema = z.union([ ChallengeResponseSchema, TokensReponseSchema ]);
export type SetNewPasswordResponse = z.infer<
  typeof SetNewPasswordResponseSchema
>;

export const useSetNewPasswordMutation = () => {
  return useMutation({
    mutationFn: async ({
      username,
      newPassword,
      session,
    }: SetNewPasswordParams) => {
      const response = await api.post(`auth/complete-new-password`, {
        body: JSON.stringify({ username, newPassword, session }),
        credentials: 'include',
      });
      return SetNewPasswordResponseSchema.parse(await response.json());
    },
  });
};

interface SetupMfaParams {
  session: string;
}

export const SetupMfaResponseSchema = z.object({
  message: z.string(),
  secretCode: z.string(),
  session: z.string(),
});
export type SetupMfaResponse = z.infer<typeof SetupMfaResponseSchema>;

export const useSetupMfaMutation = () => {
  return useMutation({
    mutationFn: async ({ session }: SetupMfaParams) => {
      const response = await api.post('auth/mfa/setup', {
        body: JSON.stringify({ session }),
      });
      return SetupMfaResponseSchema.parse(await response.json());
    },
  });
};

interface VerifyMfaParams {
  username: string;
  mfaCode: string;
  session: string;
}

export const VerifyMfaResponseSchema = z.object({
  session: z.string(),
});
export type VerifyMfaResponse = z.infer<typeof VerifyMfaResponseSchema>;

export const useVerifyMfaMutation = () => {
  return useMutation({
    mutationFn: async (params: VerifyMfaParams) => {
      const response = await api.post('auth/mfa/verify', {
        body: JSON.stringify(params),
      });
      return VerifyMfaResponseSchema.parse(await response.json());
    },
  });
};

interface RespondToMfaChallengeParams {
  username: string;
  session: string;
  mfaCode?: string;
}

export const useRespondToMfaChallengeMutation = () => {
  return useMutation({
    mutationFn: async (params: RespondToMfaChallengeParams) => {
      const response = await api.post('auth/mfa/respond', {
        body: JSON.stringify(params),
        credentials: 'include',
      });
      return TokensReponseSchema.parse(await response.json());
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getCurrentUserQueryOptions().queryKey,
      });
    },
  });
};

export const useLogoutMutation = () => {
  return useMutation({
    mutationFn: async () => {
      const response = await api.post('auth/logout', { credentials: 'include' });
      return await response.json();
    },
  });
};
